import React from 'react';
import { formatDateTimeUTC as centralizedFormatDateTimeUTC } from '../../../../helpers/formattersShapedData';
import { Cluster } from '../ClusterData';
import { GenericTableColumn } from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import CopyableValue from '@shared/components/CopyableValue';

export interface ClusterDetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode;
  type?: string;
  variant?: string;
}

export interface ClusterDetailSection {
  header: string;
  isCollapsible?: boolean;
  initialExpanded?: boolean;
  items: ClusterDetailItem[];
}

export interface CloudElement {
  id: number;
  elementType: string;
  elementId: string;
  vpcId: string;
  region: string;
  tags: string;
  discoveredAt: string;
  reflectorInstance: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Using centralized formatDateTimeUTC with legacy format
export const formatDateTimeUTC = (dateString: string) => centralizedFormatDateTimeUTC(dateString, true);

export function getClusterDetailsSection(cluster: Cluster) {
  return [
    {
      header: 'Details',
      items: [
        { label: 'ID', value: cluster.clusterID || '-' },
        { label: 'Cluster', value: cluster.clusterID || '-', type: 'tag', variant: 'neutral' },
        { label: 'Name', value: cluster.name || '-', type: 'editable' },
        { label: 'Status', value: cluster.status || '-', type: 'tag', variant: getClusterStatusVariant(cluster.status) },
        { label: 'Version', value: cluster.version || '-' },
        { label: 'Creation Date', value: cluster.createdAt ? formatDateTimeUTC(cluster.createdAt) : '-' },
        { label: 'Tag', value: cluster.tag || '-', type: 'editable', variant: 'neutral' },
        { label: 'Last Sync', value: cluster.lastSyncAt ? formatDateTimeUTC(cluster.lastSyncAt) : '-' },
        { label: 'Cloud', value: cluster.cloud || '-', type: 'tag', variant: 'warning' },
        { label: 'VPC/VNET/VCN', value: cluster.vpcId || '-' },
        { label: 'Region', value: cluster.region || '-', type: 'tag', variant: 'neutral' },
        { label: 'IAM Permissions', value: '', type: 'iam', variant: 'warning' },
      ],
    },
  ];
}

// Helper function to determine cluster status variant based on status value
function getClusterStatusVariant(status?: string): 'success' | 'warning' | 'error' | 'neutral' | 'default' {
  if (!status) return 'neutral';
  
  switch (status.toLowerCase()) {
    case 'online':
    case 'active':
      return 'success';
    case 'offline':
      return 'error';
    case 'degraded':
      return 'warning';
    default:
      return 'neutral';
  }
}


// Cloud Elements columns for cluster details
export const clusterCloudElementsColumns: GenericTableColumn<CloudElement>[] = [
  { header: 'Type', accessor: 'elementType' },
  { 
    header: 'Element ID', 
    accessor: 'elementId',
    renderCell: (row) => (
      <CopyableValue value={row.elementId || ''} />
    )
  },
  { 
    header: 'VPC', 
    accessor: 'vpcId',
    renderCell: (row) => (
      <CopyableValue value={row.vpcId || ''} />
    )
  },
  { header: 'Region', accessor: 'region' },
  { 
    header: 'Tags', 
    accessor: 'tags',
    renderCell: (row) => {
      if (!row.tags) return '-';
      try {
        // Parse the JSON string
        const tags = typeof row.tags === 'string' ? JSON.parse(row.tags) : row.tags;
        if (typeof tags === 'object' && tags !== null) {
          return (
            <div className="flex flex-wrap gap-1">
              {Object.entries(tags).map(([key, value]) => (
                <GenericTag 
                  key={key} 
                  label={`${key}: ${value}`} 
                  variant="neutral" 
                />
              ))}
            </div>
          );
        }
        return <span>{String(tags)}</span>;
      } catch (error) {
        // If JSON parsing fails, treat as comma-separated string
        const tagString = String(row.tags);
        if (tagString.includes(',')) {
          const tagArray = tagString.split(',').map(tag => tag.trim()).filter(tag => tag);
          return (
            <div className="flex flex-wrap gap-1">
              {tagArray.map((tag, index) => (
                <GenericTag 
                  key={index} 
                  label={tag} 
                  variant="neutral" 
                />
              ))}
            </div>
          );
        }
        return <GenericTag label={tagString} variant="neutral" />;
      }
    }
  },
  { header: 'Discovered At', accessor: 'discoveredAt', renderCell: (row) => formatDateTimeUTC(row.discoveredAt) },
  { header: 'Discovered By', accessor: 'reflectorInstance', renderCell: (row) => <GenericTag label={row.reflectorInstance.name} variant="neutral" /> },
]; 